import {useState, useEffect} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {setBuildPipelines, setBuildDurationThreshold, setBuild, initialBuildState} from '@/store/onboardingSlices' // adjust path if needed
import {anyWidgetFieldTouched, hasEmptyRequiredFields} from '@/components/SelfOnboarding/util/widgetErrorUtils'

type Suggestion = {id: string; label: string; fullObject: any} // value holds the id
type Pipeline = {
  buildJob: string
  buildDurationThreshold: string
  id: string
}
import {AutoCompleteWrapper} from '@/components/GDS/molecules/AutoCompleteWrapper'
import Button from '@/components/GDS/molecules/Button/Button'
import CardWrapper from '@/components/GDS/molecules/CardWrapper/Card'

import sharedStyles from '@/components/SelfOnboarding/Shared/OnboardingFormShared.module.css'
import {getBuildTitle} from '@/components/SelfOnboarding/util/getLabel'
import {Feedback, Label} from 'pepsico-ds'
import {BUILD_WIDGET, GENERAL} from '@/components/SelfOnboarding/util/constants'
import ButtonWrapper from '@/components/GDS/molecules/Button/Button'
import styles from './BuildWidget.module.css'
import type {RootState} from '@/store'
import CustomInput from '@/components/common/CustomInputWrapper'

const initialPipeline: Pipeline = {buildJob: '', buildDurationThreshold: '', id: ''}

interface BuildWidgetProps {
  onValidationChange?: (hasError: boolean) => void
}

const BuildWidget: React.FC<BuildWidgetProps> = ({onValidationChange}) => {
  const buildState = useSelector((state: any) => state.build) as {buildDurationThreshold: string; pipelines: Pipeline[]}
  const pipelines = buildState.pipelines
  const buildDurationThreshold = buildState.buildDurationThreshold
  const dispatch = useDispatch()
  const isDark = useSelector((state: RootState) => state.theme.mode === 'dark')

  // Ensure at least one pipeline in Redux
  useEffect(() => {
    if (!pipelines || pipelines.length === 0) {
      dispatch(setBuildPipelines([{...initialPipeline}]))
    }
  }, [pipelines, dispatch])
  // Maintain a separate suggestions array for each pipeline row
  const [pipelineSuggestions, setPipelineSuggestions] = useState<Suggestion[][]>([])

  // Track touched fields for each pipeline
  const [touched, setTouched] = useState<{[idx: number]: {buildJob?: boolean; buildDurationThreshold?: boolean}}>({})

  const updatePipeline = (idx: number, field: string, value: string | {id: string; label: string}) => {
    const newPipelines = pipelines.map((p, i) => (i === idx ? {...p, [field]: value} : p))
    dispatch(setBuildPipelines(newPipelines))
  }

  const updateBuildDurationThreshold = (value: string) => {
    dispatch(setBuildDurationThreshold(value))
  }

  const handleBlur = (idx: number, field: keyof Pipeline) => {
    setTouched(prev => ({
      ...prev,
      [idx]: {
        ...(prev[idx] || {}),
        [field]: true,
      },
    }))
  }

  const addPipeline = () => {
    dispatch(setBuildPipelines([...pipelines, {...initialPipeline}]))
  }

  const removePipeline = (idx: number) => {
    dispatch(setBuildPipelines(pipelines.filter((_, i) => i !== idx)))
    setTouched(prev => {
      const copy = {...prev}
      delete copy[idx]
      return copy
    })
  }

  // Validation: show errors for all required fields if any field touched
  useEffect(() => {
    const widgetTouched = anyWidgetFieldTouched(touched)
    const hasError =
      (widgetTouched && !buildDurationThreshold) || (widgetTouched && hasEmptyRequiredFields(['buildJob'], pipelines))
    if (onValidationChange) onValidationChange(hasError)
  }, [buildDurationThreshold, pipelines, touched, onValidationChange])

  // Compute widgetTouched for field-level error display
  const widgetTouched = anyWidgetFieldTouched(touched)

  // Utility to check if buildJob is empty (handles string or object)
  const isBuildJobEmpty = (buildJob: any) => {
    if (!buildJob) return true
    if (typeof buildJob === 'string') return buildJob.trim() === ''
    if (typeof buildJob === 'object') {
      return !buildJob.label && !buildJob.description
    }
    return false
  }

  return (
    <>
      <div className={styles.buildWidgetRow}>
        <div className="flex w-[282px] flex-col items-start">
          <CustomInput
            inputSize="medium"
            label={BUILD_WIDGET.durationLabel}
            onChange={e => updateBuildDurationThreshold(e.target.value)}
            onBlur={() =>
              setTouched(prev => ({
                ...prev,
                [-1]: {
                  ...(prev[-1] || {}),
                  buildDurationThreshold: true,
                },
              }))
            }
            error={widgetTouched && !buildDurationThreshold ? BUILD_WIDGET.required : ''}
            type="number"
            helperText={BUILD_WIDGET.durationHelper}
            name={'buildDurationThreshold'}
            value={buildDurationThreshold}
          />
        </div>
        <ButtonWrapper
          iconTrailing={'delete_outline'}
          variant="tertiary"
          size="small"
          onClick={() => {
            dispatch(setBuild(initialBuildState))
            setTouched({})
          }}
        />
      </div>
      {pipelines.map((pipeline, idx) => (
        <CardWrapper
          key={idx}
          cardHeader={{
            title: `${BUILD_WIDGET.label} ${idx + 1}`,
            rightAccessory:
              idx > 0
                ? {
                    iconButtons: [
                      {
                        icon: 'delete',
                        onClick: () => removePipeline(idx),
                      },
                    ],
                  }
                : undefined,
          }}
          cardBody={{
            type: 'slot',
            slot: <></>,
          }}
          state="non-interactive"
          type="basic"
        >
          <form className="flex w-full flex-col items-start">
            <div className="flex w-[282px] flex-col items-start">
              <div className="flex w-full flex-col">
                <Label className={sharedStyles.autocompleteLabelFont} label={BUILD_WIDGET.label} />
                <AutoCompleteWrapper
                  icon="keyboard_arrow_down"
                  limitSuggestions={30}
                  placeholderText={BUILD_WIDGET.placeholder}
                  suggestions={pipelineSuggestions[idx] || []}
                  isAutoComplete={true}
                  isLeadingIcon={false}
                  searchTerm={
                    typeof pipeline.buildJob === 'string'
                      ? pipeline.buildJob
                      : pipeline.buildJob && typeof pipeline.buildJob === 'object'
                        ? 'description' in pipeline.buildJob
                          ? (pipeline.buildJob as {description: string}).description
                          : 'label' in pipeline.buildJob
                            ? (pipeline.buildJob as {label: string}).label
                            : ''
                        : ''
                  }
                  onUpdate={async (query: string) => {
                    try {
                      const {searchItemsBySearchField} = await import('@/services/team/teamService')
                      const results = await searchItemsBySearchField('build', query)
                      const mapped = Array.isArray(results)
                        ? results.map((item: any) => {
                            const id = item.id || item._id || item.value || item.label
                            return {
                              id,
                              label: getBuildTitle(item),
                              fullObject: item,
                            }
                          })
                        : []
                      setPipelineSuggestions(prev => {
                        const copy = [...prev]
                        copy[idx] = mapped
                        return copy
                      })
                    } catch (err) {
                      // setError('Failed to load suggestions')
                    } finally {
                      // setLoading(false)
                    }
                  }}
                  onSelectOption={(option: any) => {
                    updatePipeline(idx, 'buildJob', option)
                  }}
                  onClear={() => updatePipeline(idx, 'buildJob', '')}
                  onBlur={() => handleBlur(idx, 'buildJob')}
                />
                <Feedback
                  variant="info"
                  className={`${sharedStyles.infoLabel} ${sharedStyles.autocompleteLabelFont}`}
                  message={BUILD_WIDGET.buildJobHelper}
                />
                {widgetTouched && isBuildJobEmpty(pipeline.buildJob) && (
                  <div className="mt-1 text-xs text-red-500">{BUILD_WIDGET.required}</div>
                )}
              </div>
            </div>
          </form>
        </CardWrapper>
      ))}
      <div className={sharedStyles.addPipelineButton}>
        <Button
          type="button"
          iconTrailing="add"
          size="medium"
          text={GENERAL.addSection}
          variant="tertiary"
          onClick={() => addPipeline()}
        />
      </div>
      <Label className={sharedStyles.infoLabel} label={BUILD_WIDGET.pipelineHelper} />
    </>
  )
}

export default BuildWidget
